'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { MarkdownEditorRestoreContextValue } from '../../../_context/MarkdownEditorRestoreContext';
import type { DbAttachment } from '../../Attachments/types';
import type { MarkdownContent } from '..';
import { clearMarkdownStorage } from './markdownStorage';
import type { MarkdownEditorState } from './useEditorLocalStorage';
export function useEditorRestoreState(value: MarkdownContent | undefined, onChange: ((v: {
  markdown: string;
  attachments: DbAttachment[];
}) => void) | undefined): MarkdownEditorRestoreContextValue {
  const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState<boolean>(false);
  const hasCompared = useRef<boolean>(false);
  // Capture the draft at detection time — autosave will overwrite localStorage
  // with the empty controlled value within 750ms, so we must not re-read on restore.
  const pendingDraftRef = useRef<MarkdownEditorState | null>(null);
  useEffect(() => {
    if (!value || hasCompared.current) return;
    hasCompared.current = true;
    if (!globalThis.window) return;
    const key = `markdownEditorContent-${globalThis.location.pathname}`;
    try {
      const savedStr = localStorage.getItem(key);
      if (!savedStr) return;
      const saved = JSON.parse(savedStr) as MarkdownEditorState;
      const originalFiles = value.attachments ?? [];
      const contentChanged = saved.markdown !== value.markdown;
      const filesChanged = (saved.attachments?.length ?? 0) !== originalFiles.length || (saved.attachments ?? []).some((f, i) => f.name !== originalFiles[i]?.name || f.url !== originalFiles[i]?.url);
      if (contentChanged || filesChanged) {
        pendingDraftRef.current = saved;
        setIsRestoreDialogOpen(true);
      }
    } catch {
      // malformed localStorage — leave editor on current value
    }
  }, [value]);
  const onRestore = useCallback(() => {
    const draft = pendingDraftRef.current;
    if (!draft) return;
    pendingDraftRef.current = null;
    onChange?.({
      markdown: draft.markdown,
      attachments: draft.attachments ?? []
    });
    setIsRestoreDialogOpen(false);
  }, [onChange]);
  const onDiscard = useCallback(() => {
    pendingDraftRef.current = null;
    clearMarkdownStorage();
    setIsRestoreDialogOpen(false);
  }, []);
  return {
    isRestoreDialogOpen,
    onRestore,
    onDiscard
  };
}